import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { getGGRReport } from '../api/client';
import { SiteFilter, siteParam, siteName, useSites } from '../components/SiteFilter';
import { ExportButtons } from '../components/ExportButtons';
import { CurrencySelector, type CurrencyMode, currencySymbol } from '../components/CurrencySelector';
import { formatAmount, formatNumber } from '../lib/format';
import { metricTip } from '../lib/metric-info';
import { Th } from '../components/Th';

interface GGRRow {
  date: string;
  site_id: number;
  currency?: string;
  ggr_real: number;
  ggr_bonus: number;
  ggr: number;
  ngr: number;
  turnover: number;
  unique_player_count: number;
}

export const GGRReport: React.FC = () => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const [data, setData] = useState<GGRRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [groupBy, setGroupBy] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [siteIds, setSiteIds] = useState<number[]>([]);
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('EUR');

  const symbol = currencySymbol(currencyMode);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getGGRReport({ date_from: dateFrom, date_to: dateTo, group_by: groupBy, site_ids: siteParam(allSites, siteIds), display_currency: currencyMode });
      setData(response.data || []);
    } catch (err) {
      console.error('Failed to load GGR report:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [dateFrom, dateTo, groupBy, siteIds, currencyMode]);

  const totalGGR = data.reduce((sum, r) => sum + (r.ggr || 0), 0);
  const totalNGR = data.reduce((sum, r) => sum + (r.ngr || 0), 0);
  const totalTurnover = data.reduce((sum, r) => sum + (r.turnover || 0), 0);
  const totalPlayers = data.reduce((sum, r) => sum + (r.unique_player_count || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('ggrReport.title')}</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">{t('ggrReport.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.from')}</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.to')}</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.groupBy')}</label>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as any)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white">
            <option value="daily">{t('filters.day')}</option>
            <option value="weekly">{t('filters.week')}</option>
            <option value="monthly">{t('filters.monthly')}</option>
          </select>
        </div>
        <SiteFilter selected={siteIds} onChange={setSiteIds} />
        <CurrencySelector selected={currencyMode} onChange={setCurrencyMode} />
        <button onClick={fetchData} disabled={loading}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-50">
          {loading ? t('filters.loading') : t('filters.apply')}
        </button>
        <ExportButtons reportType="ggr" dateFrom={dateFrom} dateTo={dateTo} siteIds={siteParam(allSites, siteIds)} />
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: t('ggrReport.totalGgr'), value: totalGGR, prefix: symbol },
          { label: t('ggrReport.totalNgr'), value: totalNGR, prefix: symbol },
          { label: t('ggrReport.totalTurnover'), value: totalTurnover, prefix: symbol },
          { label: t('ggrReport.uniquePlayers'), value: totalPlayers, prefix: '' },
        ].map((card) => (
          <div key={card.label} className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-400">{card.label}</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              {card.prefix}{card.prefix ? formatAmount(card.value) : formatNumber(card.value)}
            </p>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">{t('ggrReport.ggrVsNgrTrend')}</h2>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} />
            <YAxis stroke="#9ca3af" style={{ fontSize: '12px' }} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
              formatter={(v: number) => `${symbol}${formatNumber(v)}`} />
            <Legend />
            <Bar dataKey="ggr" name="GGR" fill="#009EA0" radius={[4, 4, 0, 0]} />
            <Bar dataKey="ngr" name="NGR" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {[
                { l: t('ggrReport.columnDate'), i: 'date' },
                { l: t('ggrReport.columnSite'), i: 'site' },
                ...(currencyMode === 'BASE' ? [{ l: t('ggrReport.columnCurrency'), i: 'currency' }] : []),
                { l: t('ggrReport.columnGgrReal'), i: 'ggrReal' },
                { l: t('ggrReport.columnGgrBonus'), i: 'ggrBonus' },
                { l: 'GGR', i: 'ggr' }, { l: 'NGR', i: 'ngr' }, { l: 'Turnover', i: 'turnover' },
                { l: t('ggrReport.columnPlayers'), i: 'uniquePlayers' },
              ].map((h) => (
                <Th key={h.l} tip={metricTip(h.i)}>{h.l}</Th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {data.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">{t('ggrReport.noDataAvailable')}</td></tr>
            ) : data.map((row, i) => (
              <tr key={i} className="bg-white dark:bg-slate-900">
                <td className="px-4 py-3 text-slate-900 dark:text-white">{row.date}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white" title={`ID ${row.site_id}`}>{siteName(allSites, row.site_id)}</td>
                {currencyMode === 'BASE' && <td className="px-4 py-3 text-slate-900 dark:text-white">{row.currency || ''}</td>}
                <td className="px-4 py-3 text-slate-900 dark:text-white">{symbol}{formatAmount(row.ggr_real || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{symbol}{formatAmount(row.ggr_bonus || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{symbol}{formatAmount(row.ggr || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{symbol}{formatAmount(row.ngr || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{symbol}{formatAmount(row.turnover || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.unique_player_count || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
